
import React, { useState } from 'react';
import { RegisterClientRequestDto } from '../../../../services/api';
import { X, Check } from 'lucide-react';

interface RegisterClientFormProps {
  onSubmit: (data: RegisterClientRequestDto) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

const AVAILABLE_SCOPES = [
  { id: 'printing:read', label: 'Ver impresiones (printing:read)' },
  { id: 'printing:write', label: 'Crear impresiones (printing:write)' },
  { id: 'printers:manage', label: 'Gestionar impresoras (printers:manage)' },
  { id: 'admin:full', label: 'Acceso total (admin:full)', warning: true },
];

const CLIENT_TYPES = [
  { id: 'PrintAgent', label: 'Print Agent' },
  { id: 'Integration', label: 'Integración' },
  { id: 'MobileApp', label: 'Aplicación Móvil' },
  { id: 'Other', label: 'Otro' },
];

export const RegisterClientForm: React.FC<RegisterClientFormProps> = ({
  onSubmit,
  onCancel,
  isLoading
}) => {
  const [formData, setFormData] = useState<RegisterClientRequestDto>({
    name: '',
    description: '',
    type: '',
    scopes: ['printing:read']
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
  };

  const toggleScope = (scopeId: string) => {
    setFormData(prev => {
      const currentScopes = prev.scopes;
      if (currentScopes.includes(scopeId)) {
        return { ...prev, scopes: currentScopes.filter(s => s !== scopeId) };
      } else {
        return { ...prev, scopes: [...currentScopes, scopeId] };
      }
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Nombre del Cliente *
        </label>
        <input
          type="text"
          required
          value={formData.name}
          onChange={e => setFormData({ ...formData, name: e.target.value })}
          placeholder="ej: Print Agent - Oficina Principal"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Descripción (opcional)
        </label>
        <textarea
          rows={3}
          value={formData.description}
          onChange={e => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Tipo *
        </label>
        <select
          required
          value={formData.type}
          onChange={e => setFormData({ ...formData, type: e.target.value })}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
        >
          <option value="">Seleccionar tipo...</option>
          {CLIENT_TYPES.map(type => (
            <option key={type.id} value={type.id}>{type.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Permisos (Scopes) *
        </label>
        <div className="space-y-2 border border-slate-200 rounded-lg p-4 bg-slate-50">
          {AVAILABLE_SCOPES.map(scope => (
            <label key={scope.id} className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.scopes.includes(scope.id)}
                onChange={() => toggleScope(scope.id)}
                className="mt-1 w-4 h-4 text-green-600 rounded border-slate-300 focus:ring-green-500"
              />
              <span className={`text-sm ${scope.warning ? 'text-red-700 font-medium' : 'text-slate-700'}`}>
                {scope.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors font-medium"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isLoading || !formData.name || !formData.type || formData.scopes.length === 0}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              Registrando...
            </>
          ) : (
            <>
              <Check size={18} />
              Registrar Cliente
            </>
          )}
        </button>
      </div>
    </form>
  );
};
